import React from 'react';
import {
  Card,
  Table,
  Tag,
  Input,
  Select,
  Space,
  Button,
  Modal,
  message,
  Row,
  Col,
  Tabs,
  Alert,
  Typography,
  Form,
  InputNumber
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  SaveOutlined,
  FileTextOutlined,
  CheckOutlined,
  CloseOutlined,
  InboxOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../store/authStore';
import { usersAPI, customerEmployeesAPI } from '../services/api';
import { API_BASE_URL } from '../services/api';
import { WorktimeAutomationCard } from './Limits';
import DocumentAcceptances from './DocumentAcceptances';
import AdminApprovals from './AdminApprovals';
import { useIsMobile } from '../hooks/useIsMobile';

const { Text } = Typography;

interface AuthUser {
  id: number;
  role: 'admin' | 'customer' | 'supplier' | 'employee' | 'support' | 'delivery';
  customerId?: number | null;
  supplierId?: number | null;
  inn?: string | null;
}

interface AdminManagedUser {
  id: number;
  email: string;
  username: string;
  fullName?: string;
  role: AuthUser['role'];
  isActive: boolean;
  createdAt: string;
  companyName?: string;
  inn?: string;
  phone?: string;
}

interface CustomerEmployeeApprovalInfo {
  id: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string | null;
  approverType?: string | null;
  approverId?: number | null;
}

interface CustomerEmployeeDocumentAcceptance {
  id: number;
  documentId: number;
  documentTitle: string;
  documentVersion: string;
  acceptedAt: string;
}

interface CustomerEmployee {
  id: number;
  email: string;
  username: string;
  role?: 'employee' | 'customer_manager' | string;
  fullName?: string | null;
  phone?: string | null;
  isActive: boolean;
  createdAt: string;
  inn?: string | null;
  companyName?: string | null;
  approval: CustomerEmployeeApprovalInfo | null;
  documentAcceptances: CustomerEmployeeDocumentAcceptance[];
  source?: 'user' | 'approvalOnly';
  limit?: {
    dailyLimit: number | null;
    monthlyLimit: number | null;
    limitSource?: 'manual' | 'auto' | null;
    appliedTier?: number | null;
    appliedWeekKey?: string | null;
    updatedAt?: string | null;
    syncStale?: boolean;
  } | null;
  syncStale?: boolean;
}

interface ApprovalUserSummary {
  id: number;
  email: string;
  username: string;
  role: string;
  isActive: boolean;
  fullName?: string | null;
}

interface CustomerEmployeeApprovalRequest {
  id: number;
  userId: number;
  companyName: string | null;
  inn: string;
  contactName: string;
  contactPhone?: string | null;
  contactEmail: string;
  status: 'pending' | 'approved' | 'rejected';
  type: 'employee' | 'delivery' | string;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string | null;
  rejectComment?: string | null;
  approverType?: string | null;
  approverId?: number | null;
  user?: ApprovalUserSummary;
}

const Users: React.FC = () => {
  const { user } = useAuthStore();

  if (!user) {
    return null;
  }

  if (user.role === 'customer') {
    return <CustomerEmployeesView currentUser={user as AuthUser} />;
  }

  return <AdminUsersView currentUser={user as AuthUser} />;
};

const AdminUsersView: React.FC<{ currentUser: AuthUser }> = ({ currentUser }) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [users, setUsers] = React.useState<AdminManagedUser[]>([]);
  const [searchText, setSearchText] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = React.useState<boolean | undefined>(undefined);
  const [statusTab, setStatusTab] = React.useState<'active' | 'deactivated' | 'all'>('active');
  const [selected, setSelected] = React.useState<AdminManagedUser | null>(null);
  const [detailsVisible, setDetailsVisible] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [editData, setEditData] = React.useState<Partial<AdminManagedUser>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка загрузки пользователей');
      }

      const data = await response.json();
      setUsers(data);
    } catch (e: any) {
      message.error('Ошибка загрузки пользователей: ' + e.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (statusTab === 'active') setStatusFilter(true);
    else if (statusTab === 'deactivated') setStatusFilter(false);
    else setStatusFilter(undefined);
  }, [statusTab]);

  const openDetails = (user: AdminManagedUser) => {
    setSelected(user);
    setEditData({});
    setEditMode(false);
    setDetailsVisible(true);
  };

  const startEdit = () => {
    if (selected) {
      setEditData(selected);
      setEditMode(true);
    }
  };

  const saveEdit = () => {
    if (!selected || !editData) return;

    const changes: Array<{ field: string; before: any; after: any }> = [];

    Object.keys(editData).forEach((key) => {
      if ((editData as any)[key] !== (selected as any)[key]) {
        changes.push({
          field:
            key === 'isActive'
              ? 'Статус аккаунта'
              : key === 'username'
              ? 'Username'
              : key === 'fullName'
              ? 'ФИО'
              : key === 'email'
              ? 'Email'
              : key === 'role'
              ? 'Роль'
              : key === 'companyName'
              ? 'Компания'
              : key === 'inn'
              ? 'ИНН'
              : key === 'phone'
              ? 'Телефон'
              : key,
          before: (selected as any)[key],
          after: (editData as any)[key]
        });
      }
    });

    if (changes.length === 0) {
      message.warning('Нет изменений для сохранения');
      return;
    }

    Modal.confirm({
      title: 'Подтвердите изменения',
      content: (
        <div>
          <p>Будут применены следующие изменения:</p>
          <ul style={{ paddingLeft: 20 }}>
            {changes.map((change, idx) => (
              <li key={idx}>
                <b>{change.field}:</b> {String(change.before)} → {String(change.after)}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText: 'Сохранить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const payload: Record<string, unknown> = {};
          (['email', 'username', 'fullName', 'phone', 'companyName', 'inn', 'role', 'isActive'] as const).forEach(
            (key) => {
              if (key in editData && (editData as any)[key] !== (selected as any)[key]) {
                payload[key] = (editData as any)[key];
              }
            }
          );
          const updated = await usersAPI.updateUser(selected.id, payload);
          message.success('Пользователь обновлен');
          setSelected({ ...selected, ...updated });
          setEditMode(false);
          setEditData({});
          load();
        } catch (error: any) {
          message.error(
            'Не удалось сохранить: ' + (error.response?.data?.error || error.message)
          );
          throw error;
        }
      }
    });
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const deleteUser = () => {
    if (!selected) return;

    if (selected.id === currentUser?.id) {
      message.error('Вы не можете архивировать свой собственный аккаунт');
      return;
    }

    const isArchived = !selected.isActive;

    if (isArchived) {
      Modal.confirm({
        title: 'Восстановить пользователя',
        content: (
          <div>
            <p>Вернуть пользователя из архива?</p>
            <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <div>
                <strong>Пользователь:</strong> {selected.username}
              </div>
            </div>
          </div>
        ),
        okText: 'Восстановить',
        cancelText: 'Отмена',
        onOk: async () => {
          try {
            await usersAPI.restoreUser(selected.id);
            message.success(`Пользователь ${selected.username} восстановлен`);
            setDetailsVisible(false);
            setEditMode(false);
            setEditData({});
            load();
          } catch (error: any) {
            message.error(
              'Ошибка при восстановлении: ' + (error.response?.data?.error || error.message)
            );
          }
        },
      });
      return;
    }

    Modal.confirm({
      title: 'В архив',
      content: (
        <div>
          <p style={{ marginBottom: 12 }}>
            Переместить пользователя в архив? Вход будет недоступен, история заказов сохранится.
          </p>
          <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <div>
              <strong>Пользователь:</strong> {selected.username}
            </div>
            <div>
              <strong>Email:</strong> {selected.email}
            </div>
            {selected.fullName && (
              <div>
                <strong>ФИО:</strong> {selected.fullName}
              </div>
            )}
            {selected.companyName && (
              <div>
                <strong>Компания:</strong> {selected.companyName}
              </div>
            )}
          </div>
        </div>
      ),
      okText: 'В архив',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await usersAPI.deleteUser(selected.id);
          message.success(`Пользователь ${selected.username} перемещён в архив`);
          setDetailsVisible(false);
          setEditMode(false);
          setEditData({});
          load();
        } catch (error: any) {
          message.error(
            'Ошибка при архивации: ' + (error.response?.data?.error || error.message)
          );
        }
      },
    });
  };

  const matchesSearch = (user: AdminManagedUser, search: string): boolean => {
    if (!search) return true;
    const searchLower = search.toLowerCase();

    if (user.email.toLowerCase().includes(searchLower)) return true;
    if (user.username.toLowerCase().includes(searchLower)) return true;
    if (user.fullName?.toLowerCase().includes(searchLower)) return true;
    if (user.companyName?.toLowerCase().includes(searchLower)) return true;
    if (user.inn?.includes(searchLower)) return true;
    if (user.phone?.includes(searchLower)) return true;

    return false;
  };

  const filteredUsers = users.filter((u) => {
    let matches = true;

    if (searchText) {
      matches = matches && matchesSearch(u, searchText);
    }

    if (roleFilter) {
      matches = matches && u.role === roleFilter;
    }

    if (statusFilter !== undefined) {
      matches = matches && u.isActive === statusFilter;
    }

    return matches;
  });

  const getRoleTag = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'red',
      customer: 'blue',
      customer_manager: 'geekblue',
      supplier: 'green',
      employee: 'cyan',
      support: 'purple',
      delivery: 'orange'
    };
    const labels: Record<string, string> = {
      admin: 'Администратор',
      customer: 'Заказчик',
      customer_manager: 'Контролёр заказчика',
      supplier: 'Поставщик',
      employee: 'Сотрудник',
      support: 'Служба поддержки',
      delivery: 'Доставщик'
    };
    return <Tag color={colors[role]}>{labels[role] || role}</Tag>;
  };

  const getStatusTag = (isActive: boolean) =>
    isActive ? <Tag color="green">Активен</Tag> : <Tag color="red">Неактивен</Tag>;

  const columns = [
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (role: string, record: AdminManagedUser) => (
        <Button type="link" style={{ padding: 0, height: 'auto', cursor: 'pointer' }} onClick={() => openDetails(record)}>
          {getRoleTag(role)}
        </Button>
      ),
      sorter: (a: AdminManagedUser, b: AdminManagedUser) => a.role.localeCompare(b.role)
    },
    {
      title: 'Компания',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 150,
      render: (company: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{
            padding: 0,
            height: 'auto',
            fontSize: 13,
            color: company ? '#1890ff' : '#999',
            cursor: 'pointer',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
          onClick={() => openDetails(record)}
        >
          {company || '—'}
        </Button>
      )
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
      width: 110,
      render: (inn: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{
            padding: 0,
            height: 'auto',
            fontSize: 13,
            color: inn ? '#1890ff' : '#999',
            fontFamily: 'monospace',
            cursor: 'pointer',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
          onClick={() => openDetails(record)}
        >
          {inn || '—'}
        </Button>
      )
    },
    {
      title: 'Пользователь',
      dataIndex: 'username',
      key: 'username',
      width: 180,
      render: (username: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: 13, cursor: 'pointer' }}
          onClick={() => openDetails(record)}
        >
          {username}
        </Button>
      ),
      sorter: (a: AdminManagedUser, b: AdminManagedUser) => a.username.localeCompare(b.username)
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: 13, cursor: 'pointer' }}
          onClick={() => openDetails(record)}
        >
          {email}
        </Button>
      ),
      sorter: (a: AdminManagedUser, b: AdminManagedUser) => a.email.localeCompare(b.email)
    },
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 130,
      render: (fullName: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: 13, fontWeight: 500 }}
          onClick={() => openDetails(record)}
        >
          {fullName || record.username}
        </Button>
      ),
      sorter: (a: AdminManagedUser, b: AdminManagedUser) =>
        (a.fullName || a.username).localeCompare(b.fullName || b.username)
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: 13, color: phone ? '#000' : '#999', cursor: 'pointer' }}
          onClick={() => openDetails(record)}
        >
          {phone || '—'}
        </Button>
      )
    },
    {
      title: 'Статус аккаунта',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, record: AdminManagedUser) => (
        <Button type="link" style={{ padding: 0, height: 'auto', cursor: 'pointer' }} onClick={() => openDetails(record)}>
          {getStatusTag(isActive)}
        </Button>
      ),
      sorter: (a: AdminManagedUser, b: AdminManagedUser) => (a.isActive ? 1 : -1) - (b.isActive ? 1 : -1)
    },
    {
      title: 'Создан',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (date: string, record: AdminManagedUser) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontSize: 13, color: '#666', cursor: 'pointer' }}
          onClick={() => openDetails(record)}
        >
          {new Date(date).toLocaleDateString('ru')}
        </Button>
      ),
      sorter: (a: AdminManagedUser, b: AdminManagedUser) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 130,
      fixed: 'right' as const,
      render: (_: any, record: AdminManagedUser) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<EditOutlined />}
          onClick={() => openDetails(record)}
          style={{ fontSize: 12 }}
        >
          Редактировать
        </Button>
      )
    }
  ];

  const tabItems = [
    {
      key: 'users',
      label: 'Пользователи',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Tabs
            activeKey={statusTab}
            onChange={(k) => setStatusTab(k as any)}
            items={[
              { key: 'active', label: 'Активные' },
              { key: 'deactivated', label: 'Деактивированные' },
              { key: 'all', label: 'Все' }
            ]}
          />
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                placeholder="Поиск по пользователю, email, ФИО, компании, ИНН или телефону..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={12} lg={12}>
              <Select
                placeholder="Фильтр по роли"
                allowClear
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ width: '100%' }}
                options={[
                  { label: 'Администратор', value: 'admin' },
                  { label: 'Заказчик', value: 'customer' },
                  { label: 'Контролёр заказчика', value: 'customer_manager' },
                  { label: 'Поставщик', value: 'supplier' },
                  { label: 'Сотрудник', value: 'employee' },
                  { label: 'Служба поддержки', value: 'support' },
                  { label: 'Доставщик', value: 'delivery' }
                ]}
              />
            </Col>
          </Row>

          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>
              Обновить
            </Button>
            <span style={{ color: '#999', fontSize: 12 }}>
              Найдено: {filteredUsers.length} из {users.length}
            </span>
          </Space>

          <Table
            rowKey="id"
            columns={columns as any}
            dataSource={filteredUsers}
            pagination={false}
            loading={loading}
            locale={{ emptyText: 'Пользователи не найдены' }}
            scroll={isMobile ? { x: 'max-content' } : { x: 1300, y: 600 }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Space>
      )
    },
    {
      key: 'approvals',
      label: 'Заявки на одобрение',
      children: <AdminApprovals />
    },
    {
      key: 'documents',
      label: (
        <span>
          <FileTextOutlined />
          Принятие документов
        </span>
      ),
      children: <DocumentAcceptances />
    }
  ];

  return (
    <Card title="Управление пользователями" loading={loading}>
      <Tabs defaultActiveKey="users" items={tabItems} />
      <Modal
        title={selected ? `Пользователь: ${selected.username}` : 'Редактирование пользователя'}
        open={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setEditMode(false);
          setEditData({});
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              {!editMode && selected && selected.id !== currentUser?.id && (
                <Button
                  danger={!!selected.isActive}
                  icon={selected.isActive ? <InboxOutlined /> : <UndoOutlined />}
                  onClick={deleteUser}
                >
                  {selected.isActive ? 'В архив' : 'Восстановить'}
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                key="close"
                onClick={() => {
                  setDetailsVisible(false);
                  setEditMode(false);
                  setEditData({});
                }}
              >
                Закрыть
              </Button>
              {editMode && (
                <>
                  <Button key="cancel" onClick={cancelEdit}>
                    Отмена
                  </Button>
                  <Button key="save" type="primary" icon={<SaveOutlined />} onClick={saveEdit}>
                    Сохранить
                  </Button>
                </>
              )}
              {!editMode && (
                <Button key="edit" type="primary" icon={<EditOutlined />} onClick={startEdit}>
                  Редактировать
                </Button>
              )}
            </div>
          </div>
        }
        width={isMobile ? '100%' : 600}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {selected && (
          <Space direction="vertical" style={{ width: '100%', paddingTop: 12 }} size="large">
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Email</div>
              {editMode ? (
                <Input value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
              ) : (
                <div style={{ fontSize: 15 }}>{selected.email}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Username</div>
              {editMode ? (
                <Input
                  value={editData.username || ''}
                  onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                />
              ) : (
                <div style={{ fontSize: 15 }}>{selected.username}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>ФИО</div>
              {editMode ? (
                <Input
                  value={editData.fullName || ''}
                  onChange={(e) => setEditData({ ...editData, fullName: e.target.value })}
                  placeholder="Не указано"
                />
              ) : (
                <div style={{ fontSize: 15 }}>{selected.fullName || 'Не указано'}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Компания</div>
              {editMode ? (
                <Input
                  value={editData.companyName || ''}
                  onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
                  placeholder="Не указана"
                />
              ) : (
                <div style={{ fontSize: 15 }}>{selected.companyName || '—'}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>ИНН</div>
              {editMode ? (
                <Input
                  value={editData.inn || ''}
                  onChange={(e) => setEditData({ ...editData, inn: e.target.value })}
                  placeholder="Не указан"
                />
              ) : (
                <div style={{ fontSize: 15 }}>{selected.inn || '—'}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Телефон</div>
              {editMode ? (
                <Input
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  placeholder="Не указан"
                />
              ) : (
                <div style={{ fontSize: 15 }}>{selected.phone || '—'}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Роль</div>
              {editMode ? (
                <Select
                  value={editData.role || selected.role}
                  onChange={(value) => setEditData({ ...editData, role: value as AuthUser['role'] })}
                  options={[
                    { label: 'Администратор', value: 'admin' },
                    { label: 'Заказчик', value: 'customer' },
                    { label: 'Контролёр заказчика', value: 'customer_manager' },
                    { label: 'Поставщик', value: 'supplier' },
                    { label: 'Сотрудник', value: 'employee' },
                    { label: 'Служба поддержки', value: 'support' },
                    { label: 'Доставщик', value: 'delivery' }
                  ]}
                />
              ) : (
                getRoleTag(selected.role)
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Статус</div>
              {editMode ? (
                <Select
                  value={editData.isActive !== undefined ? editData.isActive : selected.isActive}
                  onChange={(value) => setEditData({ ...editData, isActive: value })}
                  options={[
                    { label: 'Активен', value: true },
                    { label: 'Неактивен', value: false }
                  ]}
                />
              ) : (
                getStatusTag(selected.isActive)
              )}
            </div>
            {!editMode && (
              <div>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>Создан</div>
                <div style={{ fontSize: 14 }}>{new Date(selected.createdAt).toLocaleString('ru')}</div>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </Card>
  );
};

const CustomerEmployeesView: React.FC<{ currentUser: AuthUser }> = ({ currentUser }) => {
  const [employees, setEmployees] = React.useState<CustomerEmployee[]>([]);
  const [approvals, setApprovals] = React.useState<CustomerEmployeeApprovalRequest[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<string>('employees');
  const [limitDrafts, setLimitDrafts] = React.useState<Record<number, { dailyLimit: number | null; monthlyLimit: number | null }>>({});
  const [limitSaving, setLimitSaving] = React.useState<Record<number, boolean>>({});
  const [employeeEditVisible, setEmployeeEditVisible] = React.useState(false);
  const [employeeEditSaving, setEmployeeEditSaving] = React.useState(false);
  const [employeeEditing, setEmployeeEditing] = React.useState<CustomerEmployee | null>(null);
  const [roleSaving, setRoleSaving] = React.useState<Record<number, boolean>>({});
  const [archiveSaving, setArchiveSaving] = React.useState<Record<number, boolean>>({});
  const [employeeStatusFilter, setEmployeeStatusFilter] = React.useState<'active' | 'archived' | 'all'>('active');
  const [employeeEditForm] = Form.useForm();

  const loadEmployees = React.useCallback(async () => {
    setLoading(true);
    try {
      const [employeeData, approvalData] = await Promise.all([
        customerEmployeesAPI.getEmployees(),
        customerEmployeesAPI.getApprovals()
      ]);
      setEmployees(employeeData || []);
      setApprovals(approvalData || []);
    } catch (error: any) {
      message.error('Не удалось загрузить сотрудников: ' + (error.response?.data?.error || error.message));
      setEmployees([]);
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const approvedEmployees = React.useMemo(
    () =>
      employees.filter(
        (employee) => !employee.approval || employee.approval.status === 'approved'
      ),
    [employees]
  );

  React.useEffect(() => {
    const drafts: Record<number, { dailyLimit: number | null; monthlyLimit: number | null }> = {};
    approvedEmployees.forEach((employee) => {
      drafts[employee.id] = {
        dailyLimit: employee.limit?.dailyLimit ?? null,
        monthlyLimit: employee.limit?.monthlyLimit ?? null
      };
    });
    setLimitDrafts(drafts);
  }, [approvedEmployees]);

  const combinedEmployees = React.useMemo(() => {
    const result: CustomerEmployee[] = approvedEmployees.map((employee) => ({
      ...employee,
      source: 'user'
    }));
    const existingIds = new Set(result.map((employee) => employee.id));
    approvals
      .filter((approval) => approval.type === 'employee' && approval.status === 'approved')
      .forEach((approval) => {
        if (!existingIds.has(approval.userId)) {
          result.push({
            id: approval.userId,
            email: approval.contactEmail,
            username: approval.user?.username || approval.contactEmail || `user-${approval.userId}`,
            role: approval.user?.role || 'employee',
            fullName: approval.contactName || approval.user?.fullName || null,
            phone: approval.contactPhone || null,
            isActive: approval.user?.isActive ?? false,
            createdAt: approval.createdAt,
            inn: approval.inn,
            companyName: approval.companyName,
            approval: {
              id: approval.id,
              status: approval.status,
              createdAt: approval.createdAt,
              updatedAt: approval.updatedAt,
              reviewedAt: approval.reviewedAt,
              approverType: approval.approverType,
              approverId: approval.approverId
            },
            documentAcceptances: [],
            source: 'approvalOnly'
          });
          existingIds.add(approval.userId);
        }
      });
    return result;
  }, [approvedEmployees, approvals]);

  const getEmployeeRoleTag = (role?: string) => {
    if (role === 'customer_manager') return <Tag color="purple">Контролёр</Tag>;
    return <Tag color="cyan">Сотрудник</Tag>;
  };

  const canChangeRole = (employee: CustomerEmployee) => {
    // Меняем роль только реальным пользователям из таблицы users (не "approvalOnly")
    if (employee.source !== 'user') return false;
    // Только для активных/одобренных сотрудников — чтобы не конфликтовать с заявками
    if (employee.approval && employee.approval.status !== 'approved') return false;
    return employee.role === 'employee' || employee.role === 'customer_manager';
  };

  const changeEmployeeRole = async (employee: CustomerEmployee, nextRole: 'employee' | 'customer_manager') => {
    setRoleSaving((prev) => ({ ...prev, [employee.id]: true }));
    try {
      await customerEmployeesAPI.setRole(employee.id, nextRole);
      message.success(nextRole === 'customer_manager' ? 'Роль изменена: Контролёр' : 'Роль изменена: Сотрудник');
      await loadEmployees();
    } catch (error: any) {
      message.error('Не удалось изменить роль: ' + (error.response?.data?.error || error.message));
    } finally {
      setRoleSaving((prev) => ({ ...prev, [employee.id]: false }));
    }
  };

  const filteredEmployees = React.useMemo(() => {
    let list = combinedEmployees;
    if (employeeStatusFilter === 'active') {
      list = list.filter((e) => e.isActive);
    } else if (employeeStatusFilter === 'archived') {
      list = list.filter((e) => !e.isActive);
    }
    if (!searchText) return list;
    const lower = searchText.toLowerCase();
    return list.filter((employee) => {
      return (
        employee.fullName?.toLowerCase().includes(lower) ||
        employee.username.toLowerCase().includes(lower) ||
        employee.email.toLowerCase().includes(lower) ||
        employee.phone?.toLowerCase().includes(lower) ||
        employee.inn?.toLowerCase().includes(lower) ||
        employee.companyName?.toLowerCase().includes(lower) ||
        (employee.role === 'customer_manager'
          ? 'контролер'.includes(lower) || 'контролёр'.includes(lower)
          : 'сотрудник'.includes(lower))
      );
    });
  }, [combinedEmployees, searchText, employeeStatusFilter]);

  const archiveEmployee = (employee: CustomerEmployee, archived: boolean) => {
    Modal.confirm({
      title: archived ? 'В архив' : 'Восстановить сотрудника',
      content: archived
        ? `Переместить «${employee.fullName || employee.username}» в архив? Сотрудник не сможет войти, история заказов сохранится.`
        : `Восстановить «${employee.fullName || employee.username}» из архива?`,
      okText: archived ? 'В архив' : 'Восстановить',
      okType: archived ? 'danger' : 'primary',
      cancelText: 'Отмена',
      onOk: async () => {
        setArchiveSaving((prev) => ({ ...prev, [employee.id]: true }));
        try {
          await customerEmployeesAPI.setArchived(employee.id, archived);
          message.success(
            archived
              ? 'Сотрудник перемещён в архив'
              : 'Сотрудник восстановлен'
          );
          await loadEmployees();
        } catch (error: any) {
          message.error(
            'Не удалось изменить статус: ' + (error.response?.data?.error || error.message)
          );
        } finally {
          setArchiveSaving((prev) => ({ ...prev, [employee.id]: false }));
        }
      },
    });
  };

  const pendingCount = React.useMemo(
    () =>
      approvals.filter((approval) => approval.type === 'employee' && approval.status === 'pending').length,
    [approvals]
  );

  const getAccountStatusTag = (isActive: boolean) =>
    isActive ? <Tag color="green">Активен</Tag> : <Tag color="default">В архиве</Tag>;

  const getApprovalStatusTag = (status?: string) => {
    if (!status) {
      return <Tag color="default">Нет заявки</Tag>;
    }

    switch (status) {
      case 'approved':
        return <Tag color="green">Одобрен</Tag>;
      case 'rejected':
        return <Tag color="red">Отклонен</Tag>;
      default:
        return <Tag color="orange">Ожидает</Tag>;
    }
  };

  const handleLimitChange = (employeeId: number, field: 'dailyLimit' | 'monthlyLimit', value: number | null) => {
    setLimitDrafts((prev) => {
      const existing = prev[employeeId] || { dailyLimit: null, monthlyLimit: null };
      const nextValue = value === null || value === undefined ? null : Number(value);
      return {
        ...prev,
        [employeeId]: {
          ...existing,
          [field]: nextValue
        }
      };
    });
  };

  const handleResetLimit = (employeeId: number) => {
    setLimitDrafts((prev) => ({
      ...prev,
      [employeeId]: {
        dailyLimit: null,
        monthlyLimit: null
      }
    }));
  };

  const isLimitChanged = (employee: CustomerEmployee) => {
    const draft = limitDrafts[employee.id];
    if (!draft) {
      return false;
    }
    const currentDaily = employee.limit?.dailyLimit ?? null;
    const currentMonthly = employee.limit?.monthlyLimit ?? null;
    return (draft.dailyLimit ?? null) !== currentDaily || (draft.monthlyLimit ?? null) !== currentMonthly;
  };

  const hasDraftValue = (employeeId: number) => {
    const draft = limitDrafts[employeeId];
    if (!draft) return false;
    return (draft.dailyLimit ?? null) !== null || (draft.monthlyLimit ?? null) !== null;
  };

  const saveLimit = async (employeeId: number) => {
    const draft = limitDrafts[employeeId] || { dailyLimit: null, monthlyLimit: null };
    setLimitSaving((prev) => ({ ...prev, [employeeId]: true }));
    try {
      await customerEmployeesAPI.setLimit(employeeId, {
        dailyLimit: draft.dailyLimit ?? null,
        monthlyLimit: draft.monthlyLimit ?? null
      });
      message.success('Лимиты обновлены');
      await loadEmployees();
    } catch (error: any) {
      message.error('Не удалось сохранить лимиты: ' + (error.response?.data?.error || error.message));
    } finally {
      setLimitSaving((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  const employeeColumns = [
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      render: (_: any, record: CustomerEmployee) => (
        <div>
          <Text strong>{record.fullName || record.username}</Text>
          <div style={{ fontSize: 12, color: '#999' }}>{record.username}</div>
        </div>
      ),
      sorter: (a: CustomerEmployee, b: CustomerEmployee) =>
        (a.fullName || a.username).localeCompare(b.fullName || b.username)
    },
    {
      title: 'Роль',
      key: 'role',
      width: 120,
      render: (_: any, record: CustomerEmployee) => getEmployeeRoleTag(record.role)
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      render: (email: string) => <span style={{ fontFamily: 'monospace' }}>{email}</span>,
      sorter: (a: CustomerEmployee, b: CustomerEmployee) => a.email.localeCompare(b.email)
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (phone: string | null | undefined) => phone || '—'
    },
    {
      title: 'Статус аккаунта',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      render: (isActive: boolean) => getAccountStatusTag(isActive),
      sorter: (a: CustomerEmployee, b: CustomerEmployee) => (a.isActive ? 1 : -1) - (b.isActive ? 1 : -1)
    },
    {
      title: 'Статус заявки',
      key: 'approval',
      width: 150,
      render: (_: any, record: CustomerEmployee) => getApprovalStatusTag(record.approval?.status),
      sorter: (a: CustomerEmployee, b: CustomerEmployee) => {
        const order = { approved: 3, pending: 2, rejected: 1, undefined: 0 } as Record<string, number>;
        return (order[a.approval?.status || 'undefined'] || 0) - (order[b.approval?.status || 'undefined'] || 0);
      }
    },
    {
      title: 'Принятые документы',
      key: 'documents',
      width: 160,
      render: (_: any, record: CustomerEmployee) => (
        <Tag color={record.documentAcceptances.length > 0 ? 'blue' : 'default'}>
          {record.documentAcceptances.length > 0
            ? `${record.documentAcceptances.length} шт.`
            : 'Нет подтверждений'}
        </Tag>
      )
    },
    {
      title: 'Дата регистрации',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleDateString('ru'),
      sorter: (a: CustomerEmployee, b: CustomerEmployee) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 380,
      fixed: 'right' as const,
      render: (_: any, record: CustomerEmployee) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={!record.isActive}
            onClick={() => {
              setEmployeeEditing(record);
              employeeEditForm.setFieldsValue({
                fullName: record.fullName ?? '',
                email: record.email ?? '',
                phone: record.phone ?? ''
              });
              setEmployeeEditVisible(true);
            }}
          >
            Редактировать
          </Button>
          <Button
            size="small"
            icon={record.role === 'customer_manager' ? <CloseOutlined /> : <CheckOutlined />}
            disabled={!canChangeRole(record) || !record.isActive}
            loading={!!roleSaving[record.id]}
            onClick={() => {
              const nextRole: 'employee' | 'customer_manager' =
                record.role === 'customer_manager' ? 'employee' : 'customer_manager';
              Modal.confirm({
                title: 'Подтвердите смену роли',
                content:
                  nextRole === 'customer_manager'
                    ? 'Назначить сотрудника контролёром заказчика?'
                    : 'Снять роль контролёра и сделать сотрудника обычным сотрудником?',
                okText: 'Подтвердить',
                cancelText: 'Отмена',
                onOk: () => changeEmployeeRole(record, nextRole)
              });
            }}
          >
            {record.role === 'customer_manager' ? 'Снять контролёра' : 'Назначить контролёром'}
          </Button>
          <Button
            size="small"
            danger={record.isActive}
            icon={record.isActive ? <InboxOutlined /> : <UndoOutlined />}
            loading={!!archiveSaving[record.id]}
            onClick={() => archiveEmployee(record, record.isActive)}
          >
            {record.isActive ? 'В архив' : 'Восстановить'}
          </Button>
        </Space>
      )
    }
  ];

  const submitEmployeeEdit = async () => {
    if (!employeeEditing) return;
    try {
      const values = await employeeEditForm.validateFields();
      setEmployeeEditSaving(true);
      await customerEmployeesAPI.updateEmployee(employeeEditing.id, values);
      message.success('Данные сотрудника обновлены');
      setEmployeeEditVisible(false);
      setEmployeeEditing(null);
      employeeEditForm.resetFields();
      await loadEmployees();
    } catch (error: any) {
      // validateFields throws; ignore
      if (error?.errorFields) return;
      message.error('Не удалось обновить сотрудника: ' + (error.response?.data?.error || error.message));
    } finally {
      setEmployeeEditSaving(false);
    }
  };

  const employeesTab = (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Row gutter={16}>
        <Col xs={24} sm={24} md={10}>
          <Input
            placeholder="Поиск по ФИО, email, телефону или ИНН"
            prefix={<SearchOutlined />}
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            value={employeeStatusFilter}
            style={{ width: '100%' }}
            onChange={(v: 'active' | 'archived' | 'all') => setEmployeeStatusFilter(v)}
            options={[
              { value: 'active', label: 'Активные' },
              { value: 'archived', label: 'В архиве' },
              { value: 'all', label: 'Все' },
            ]}
          />
        </Col>
        <Col xs={24} sm={12} md={8} style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <Tag color="blue">Всего: {combinedEmployees.length}</Tag>
          <Tag color={pendingCount > 0 ? 'orange' : 'default'}>Ожидают: {pendingCount}</Tag>
          <Button icon={<ReloadOutlined />} onClick={loadEmployees}>
            Обновить
          </Button>
        </Col>
      </Row>
      <Table
        rowKey="id"
        columns={employeeColumns as any}
        dataSource={filteredEmployees}
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'Сотрудники не найдены' }}
        // Как во вкладке «Лимиты»: без внутреннего вертикального скролла,
        // чтобы список был "единым" и прокручивался страницей.
        scroll={{ x: 1100 }}
      />

      <Modal
        title={employeeEditing ? `Редактирование: ${employeeEditing.fullName || employeeEditing.username}` : 'Редактирование сотрудника'}
        open={employeeEditVisible}
        onCancel={() => {
          setEmployeeEditVisible(false);
          setEmployeeEditing(null);
          employeeEditForm.resetFields();
        }}
        onOk={submitEmployeeEdit}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={employeeEditSaving}
      >
        <Form form={employeeEditForm} layout="vertical">
          <Form.Item
            label="ФИО"
            name="fullName"
            rules={[{ required: true, message: 'Введите ФИО сотрудника' }]}
          >
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item label="Телефон" name="phone">
            <Input placeholder="+7 (999) 123-45-67" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );

  const isLimitStale = (record: CustomerEmployee) =>
    !!(record.syncStale || record.limit?.syncStale);

  const limitColumns = [
    {
      title: 'Сотрудник',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 240,
      render: (_: any, record: CustomerEmployee) => (
        <div>
          <Text strong>{record.fullName || record.username}</Text>
          <div style={{ fontSize: 12, color: '#999' }}>{record.email}</div>
          {record.limit?.limitSource === 'auto' && <Tag color="blue" style={{ marginTop: 4 }}>Авто</Tag>}
          {record.limit?.limitSource === 'manual' && <Tag style={{ marginTop: 4 }}>Вручную</Tag>}
        </div>
      )
    },
    {
      title: 'Дневной лимит, ₽',
      key: 'dailyLimit',
      width: 180,
      render: (_: any, record: CustomerEmployee) => (
        <div style={isLimitStale(record) ? { outline: '2px solid #ff4d4f', borderRadius: 6, padding: 4 } : undefined}>
          <InputNumber
            min={0}
            precision={2}
            style={{ width: '100%' }}
            value={limitDrafts[record.id]?.dailyLimit ?? null}
            placeholder="Не задан"
            onChange={(value) => handleLimitChange(record.id, 'dailyLimit', value)}
          />
        </div>
      )
    },
    {
      title: 'Месячный лимит, ₽',
      key: 'monthlyLimit',
      width: 180,
      render: (_: any, record: CustomerEmployee) => (
        <div style={isLimitStale(record) ? { outline: '2px solid #ff4d4f', borderRadius: 6, padding: 4 } : undefined}>
          <InputNumber
            min={0}
            precision={2}
            style={{ width: '100%' }}
            value={limitDrafts[record.id]?.monthlyLimit ?? null}
            placeholder="Не задан"
            onChange={(value) => handleLimitChange(record.id, 'monthlyLimit', value)}
          />
        </div>
      )
    },
    {
      title: 'Обновлено',
      key: 'updatedAt',
      width: 180,
      render: (_: any, record: CustomerEmployee) =>
        record.limit?.updatedAt ? new Date(record.limit.updatedAt).toLocaleString('ru') : '—'
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 220,
      render: (_: any, record: CustomerEmployee) => (
        <Space>
          <Button
            type="primary"
            onClick={() => saveLimit(record.id)}
            disabled={!isLimitChanged(record)}
            loading={!!limitSaving[record.id]}
          >
            Сохранить
          </Button>
          <Button onClick={() => handleResetLimit(record.id)} disabled={!hasDraftValue(record.id)}>
            Сбросить
          </Button>
        </Space>
      )
    }
  ];

  const limitsTab = (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <WorktimeAutomationCard compact />
      <Alert
        type="info"
        showIcon
        message="Лимиты сотрудников"
        description={
          <span>
            Ручная правка ниже имеет приоритет над автоматикой, пока вы не снимете лимит (пустые поля + Сохранить).
            {' '}
            <Link to="/limits">Полные настройки автоматизации и привязка к WorkTime →</Link>
          </span>
        }
      />
      <Table
        rowKey="id"
        columns={limitColumns as any}
        dataSource={approvedEmployees}
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'Нет сотрудников для настройки лимитов' }}
        scroll={{ x: 900 }}
      />
    </Space>
  );

  const approvalsLabel = (
    <span>
      Заявки на одобрение
      {pendingCount > 0 && (
        <Tag color="orange" style={{ marginLeft: 8 }}>
          {pendingCount}
        </Tag>
      )}
    </span>
  );

  const tabItems = [
    { key: 'employees', label: `Сотрудники (${combinedEmployees.length})`, children: employeesTab },
    { key: 'limits', label: 'Лимиты', children: limitsTab },
    {
      key: 'approvals',
      label: approvalsLabel,
      children: (
        <CustomerEmployeeApprovals
          approvals={approvals}
          loading={loading}
          onProcessed={loadEmployees}
        />
      )
    }
  ];

  return (
    <Card title="Мои сотрудники">
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Card>
  );
};

const CustomerEmployeeApprovals: React.FC<{
  approvals: CustomerEmployeeApprovalRequest[];
  loading?: boolean;
  onProcessed?: () => Promise<void> | void;
}> = ({ approvals, loading = false, onProcessed }) => {
  const [activeStatus, setActiveStatus] = React.useState<'pending' | 'approved' | 'rejected'>('pending');
  const [searchText, setSearchText] = React.useState('');
  const [rejectModalVisible, setRejectModalVisible] = React.useState(false);
  const [rejectComment, setRejectComment] = React.useState('');
  const [selectedApproval, setSelectedApproval] = React.useState<CustomerEmployeeApprovalRequest | null>(null);
  const [editModalVisible, setEditModalVisible] = React.useState(false);
  const [editingApproval, setEditingApproval] = React.useState<CustomerEmployeeApprovalRequest | null>(null);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [editForm] = Form.useForm();

  const employeeApprovals = React.useMemo(
    () => approvals.filter((approval) => approval.type === 'employee'),
    [approvals]
  );

  const matchesSearch = (approval: CustomerEmployeeApprovalRequest) => {
    if (!searchText) return true;
    const lower = searchText.toLowerCase();
    return (
      approval.contactName.toLowerCase().includes(lower) ||
      approval.contactEmail.toLowerCase().includes(lower) ||
      approval.inn.toLowerCase().includes(lower) ||
      approval.companyName?.toLowerCase().includes(lower)
    );
  };

  const getDataForStatus = (status: 'pending' | 'approved' | 'rejected') =>
    employeeApprovals.filter((approval) => approval.status === status && matchesSearch(approval));

  const getStatusCount = (status: 'pending' | 'approved' | 'rejected') =>
    employeeApprovals.filter((approval) => approval.status === status).length;

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'approved':
        return <Tag color="green">Одобрена</Tag>;
      case 'rejected':
        return <Tag color="red">Отклонена</Tag>;
      default:
        return <Tag color="orange">Ожидает</Tag>;
    }
  };

  const handleApprove = (approval: CustomerEmployeeApprovalRequest) => {
    Modal.confirm({
      title: 'Одобрить заявку',
      content: `Подтвердить сотрудника "${approval.contactName}"?`,
      okText: 'Одобрить',
      cancelText: 'Отмена',
      async onOk() {
        try {
          setActionLoading(true);
          await customerEmployeesAPI.approve(approval.id);
          message.success('Заявка одобрена');
          if (onProcessed) {
            await onProcessed();
          }
        } catch (error: any) {
          message.error('Ошибка при одобрении: ' + (error.response?.data?.error || error.message));
          throw error;
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const openRejectModal = (approval: CustomerEmployeeApprovalRequest) => {
    setSelectedApproval(approval);
    setRejectComment('');
    setRejectModalVisible(true);
  };

  const confirmReject = async () => {
    if (!selectedApproval) return;

    if (!rejectComment.trim()) {
      message.warning('Необходимо указать причину отклонения');
      return;
    }

    try {
      setActionLoading(true);
      await customerEmployeesAPI.reject(selectedApproval.id, rejectComment);
      message.success('Заявка отклонена');
      setRejectModalVisible(false);
      setRejectComment('');
      setSelectedApproval(null);
      if (onProcessed) {
        await onProcessed();
      }
    } catch (error: any) {
      message.error('Ошибка при отклонении: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevert = (approval: CustomerEmployeeApprovalRequest) => {
    Modal.confirm({
      title: 'Вернуть заявку',
      content: 'Вернуть заявку на повторное рассмотрение?',
      okText: 'Вернуть',
      cancelText: 'Отмена',
      async onOk() {
        try {
          setActionLoading(true);
          await customerEmployeesAPI.revert(approval.id, 'pending');
          message.success('Заявка возвращена на рассмотрение');
          if (onProcessed) {
            await onProcessed();
          }
        } catch (error: any) {
          message.error('Ошибка при возврате: ' + (error.response?.data?.error || error.message));
          throw error;
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const openEditModal = (approval: CustomerEmployeeApprovalRequest) => {
    setEditingApproval(approval);
    editForm.setFieldsValue({
      fullName: approval.contactName || '',
      email: approval.contactEmail || '',
      phone: approval.contactPhone || ''
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    if (!editingApproval) return;
    try {
      const values = await editForm.validateFields();
      setActionLoading(true);
      await customerEmployeesAPI.updateEmployee(editingApproval.userId, values);
      message.success('Данные заявки обновлены');
      setEditModalVisible(false);
      setEditingApproval(null);
      editForm.resetFields();
      if (onProcessed) {
        await onProcessed();
      }
    } catch (error: any) {
      message.error('Ошибка при сохранении: ' + (error.response?.data?.error || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const approvalColumns = [
    {
      title: 'Сотрудник',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 220,
      render: (text: string) => <Text strong>{text}</Text>,
      sorter: (a: CustomerEmployeeApprovalRequest, b: CustomerEmployeeApprovalRequest) =>
        a.contactName.localeCompare(b.contactName)
    },
    {
      title: 'Email',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
      width: 220,
      render: (email: string) => <span style={{ fontFamily: 'monospace' }}>{email}</span>
    },
    {
      title: 'Телефон',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 160,
      render: (phone: string | null | undefined) => phone || '—'
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
      width: 140,
      render: (inn: string) => <span style={{ fontFamily: 'monospace' }}>{inn}</span>
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => getStatusTag(status),
      sorter: (a: CustomerEmployeeApprovalRequest, b: CustomerEmployeeApprovalRequest) =>
        a.status.localeCompare(b.status)
    },
    {
      title: 'Дата заявки',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleDateString('ru'),
      sorter: (a: CustomerEmployeeApprovalRequest, b: CustomerEmployeeApprovalRequest) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 280,
      render: (_: any, record: CustomerEmployeeApprovalRequest) => (
        <Space wrap>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleApprove(record)}
            disabled={record.status !== 'pending' || actionLoading}
          >
            Одобрить
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => openRejectModal(record)}
            disabled={record.status !== 'pending' || actionLoading}
          >
            Отклонить
          </Button>
          {record.status !== 'approved' && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              disabled={actionLoading}
            >
              Редактировать
            </Button>
          )}
          {record.status === 'rejected' && (
            <Button size="small" onClick={() => handleRevert(record)} disabled={actionLoading}>
              Вернуть
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Row gutter={16}>
        <Col xs={24} sm={24} md={12}>
          <Input
            placeholder="Поиск по сотруднику, email, ИНН"
            prefix={<SearchOutlined />}
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
      </Row>
      <Tabs
        activeKey={activeStatus}
        onChange={(key) => setActiveStatus(key as 'pending' | 'approved' | 'rejected')}
        items={[
          {
            key: 'pending',
            label: `Ожидают (${getStatusCount('pending')})`,
            children: (
              <Table
                rowKey="id"
                columns={approvalColumns as any}
                dataSource={getDataForStatus('pending')}
                pagination={false}
                loading={loading || actionLoading}
                scroll={{ x: 1100, y: 600 }}
              />
            )
          },
          {
            key: 'approved',
            label: `Одобрены (${getStatusCount('approved')})`,
            children: (
              <Table
                rowKey="id"
                columns={approvalColumns as any}
                dataSource={getDataForStatus('approved')}
                pagination={false}
                loading={loading || actionLoading}
                scroll={{ x: 1100, y: 600 }}
              />
            )
          },
          {
            key: 'rejected',
            label: `Отклонены (${getStatusCount('rejected')})`,
            children: (
              <Table
                rowKey="id"
                columns={approvalColumns as any}
                dataSource={getDataForStatus('rejected')}
                pagination={false}
                loading={loading || actionLoading}
                scroll={{ x: 1100, y: 600 }}
              />
            )
          }
        ]}
      />

      <Modal
        title="Отклонение заявки"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectComment('');
          setSelectedApproval(null);
        }}
        onOk={confirmReject}
        okText="Отклонить"
        cancelText="Отмена"
        okButtonProps={{ danger: true, loading: actionLoading }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Заявка от: <strong>{selectedApproval?.contactName}</strong>
          </Text>
          <Input.TextArea
            rows={4}
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder="Укажите причину отклонения (это сообщение увидит сотрудник)"
          />
        </Space>
      </Modal>

      <Modal
        title="Редактирование заявки"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingApproval(null);
          editForm.resetFields();
        }}
        onOk={handleEditSubmit}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={actionLoading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="ФИО"
            name="fullName"
            rules={[{ required: true, message: 'Введите ФИО сотрудника' }]}
          >
            <Input placeholder="Иванов Иван Иванович" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item
            label="Телефон"
            name="phone"
            rules={[
              { required: true, message: 'Введите телефон' },
              { pattern: /^\+?7[\s-]?9[\d\s-]{8,}$/, message: 'Некорректный номер телефона' }
            ]}
          >
            <Input placeholder="+7 (999) 123-45-67" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default Users;

